import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Gift, Users, DollarSign, Copy, CheckCircle, 
  Share2, Mail, MessageSquare, Trophy, Zap,
  TrendingUp, Clock
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralProgram = () => {
  const [userEmail, setUserEmail] = useState('');
  const [referralData, setReferralData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const fetchReferralCode = async () => {
    if (!userEmail || !userEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    setLoading(true);
    try {
      // Try the enhanced v2 API first, fallback to original
      let res;
      try {
        res = await axios.post(`${API}/referrals-v2/create-code`, { 
          email: userEmail,
          user_type: 'driver'
        });
        setReferralData({ code: res.data.code, share_link: res.data.share_link });
      } catch {
        res = await axios.get(`${API}/referrals/code/${encodeURIComponent(userEmail)}`);
        setReferralData(res.data);
      }
      
      // Fetch stats from enhanced API
      try {
        const statsRes = await axios.get(`${API}/referrals-v2/stats/${encodeURIComponent(userEmail)}`);
        if (statsRes.data.has_code) {
          setStats(statsRes.data.stats);
        }
      } catch {
        const statsRes = await axios.get(`${API}/referrals/stats/${encodeURIComponent(userEmail)}`);
        setStats(statsRes.data);
      }
      
      toast.success('Referral code retrieved!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to get referral code');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = `${window.location.origin}/driver-signup?ref=${referralData?.code || ''}`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Referral link copied!');
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="p-6 max-w-6xl mx-auto" data-testid="referral-program-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Referral Program</h1>
          <p className="text-gray-400 max-w-xl mx-auto">
            Earn $50 for every carrier you refer who subscribes to a paid plan. No limits!
          </p>
        </div>

        {/* Get Your Code Section */}
        {!referralData && (
          <Card className="bg-slate-800/50 border-slate-700 max-w-md mx-auto mb-8" data-testid="get-code-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" />
                Get Your Referral Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="referral-email-input"
                />
              </div>
              <Button 
                onClick={fetchReferralCode}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                data-testid="get-code-btn"
              >
                {loading ? 'Loading...' : 'Get My Referral Code'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Main Dashboard */}
        {referralData && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Your Code */}
            <div className="lg:col-span-2 space-y-6">
              {/* Referral Code Card */}
              <Card className="bg-gradient-to-br from-orange-500/20 to-amber-500/20 border-orange-500/30" data-testid="referral-code-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Your Referral Code</h3>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setReferralData(null);
                        setStats(null);
                        setUserEmail('');
                      }}
                      className="text-gray-400"
                    >
                      Change Email
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 bg-slate-900/50 rounded-lg px-4 py-3 font-mono text-2xl text-orange-400 text-center">
                      {referralData.code}
                    </div>
                    <Button 
                      onClick={copyCode}
                      variant="outline"
                      className={copied ? 'bg-green-500/20 border-green-500 text-green-400' : ''}
                      data-testid="copy-code-btn"
                    >
                      {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={copyLink}
                      className="flex items-center gap-2"
                      data-testid="copy-link-btn"
                    >
                      <Share2 className="w-4 h-4" /> Copy Link
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(`mailto:?subject=Join Tempus Dispatch&body=Use my referral code ${referralData.code} to sign up and we both earn $50! ${shareLink}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" /> Email
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => window.open(`sms:?body=Hey! Use my referral code ${referralData.code} to sign up for Tempus Dispatch. We both earn $50! ${shareLink}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4" /> Text
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid */}
              <div className="grid sm:grid-cols-3 gap-4">
                <Card className="bg-slate-800/50 border-slate-700" data-testid="total-referrals-stat">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
                    <p className="text-sm text-gray-400">Total Referrals</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700" data-testid="pending-referrals-stat">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">{stats?.pending_referrals || 0}</p>
                    <p className="text-sm text-gray-400">Pending</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700" data-testid="earnings-stat">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-white">${stats?.total_earnings || 0}</p>
                    <p className="text-sm text-gray-400">Total Earned</p>
                  </CardContent>
                </Card>
              </div>

              {/* Leaderboard Preview */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-yellow-500" />
                    How It Works
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 font-bold">1</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Share Your Code</h4>
                        <p className="text-sm text-gray-400">Send your unique code to drivers you know</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-400 font-bold">2</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">They Sign Up</h4>
                        <p className="text-sm text-gray-400">New driver registers using your referral code</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-400 font-bold">3</span>
                      </div>
                      <div>
                        <h4 className="text-white font-medium">Earn $50</h4>
                        <p className="text-sm text-gray-400">Get paid when they complete their first load</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Promo */}
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
                <CardContent className="p-6 text-center">
                  <DollarSign className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-4xl font-bold text-white mb-1">$50</p>
                  <p className="text-green-400 font-medium">Per Referral</p>
                  <p className="text-sm text-gray-400 mt-2">No limits on earnings!</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Top Earners
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { name: 'Mike T.', referrals: 23, earnings: 1150 },
                    { name: 'Sarah K.', referrals: 18, earnings: 900 },
                    { name: 'Carlos R.', referrals: 15, earnings: 750 },
                  ].map((user, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          i === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                          i === 1 ? 'bg-gray-400/20 text-gray-300' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {i + 1}
                        </div>
                        <span className="text-white">{user.name}</span>
                      </div>
                      <span className="text-green-400 font-medium">${user.earnings}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-400 text-center">
                    Need help? Contact us at{' '}
                    <a href="mailto:support@tempusdispatch.com" className="text-blue-400 hover:underline">
                      support@tempusdispatch.com
                    </a>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReferralProgram;
